<section
  class="dates-radar"
  data-section-id="homeia_component_dates_radar"
  *ngIf="!loading && !error && !empty && summary as data; else datesState"
>
  <header class="dates-header">
    <div class="copy-block">
      <div class="eyebrow-row">
        <p class="section-eyebrow">{{ lang.translate('home.datesSummary.eyebrow') }}</p>
        <span class="ia-badge">{{ lang.translate('home.shared.detectedBadge') }}</span>
      </div>
      <h2>{{ lang.translate('home.datesSummary.title') }}</h2>
    </div>
    <button
      type="button"
      class="total-pill"
      (click)="onViewDatesPanel()"
      [attr.aria-expanded]="(datesPanelOpen$ | async) ? 'true' : 'false'"
      [attr.title]="lang.translate('home.datesSummary.tooltip')"
    >
      <span class="count">{{ data.totalDetected }}</span>
      <span class="label">{{ lang.translate('home.datesSummary.totalLabel') }}</span>
    </button>
  </header>

  <p class="section-copy">{{ lang.translate('home.datesSummary.description') }}</p>
  <p class="connection-copy">{{ lang.translate('home.datesSummary.connection') }}</p>
  <div class="action-feedback" *ngIf="filterMessageKey || filterError">
    <p class="action-success" *ngIf="filterMessageKey">
      {{ lang.translate(filterMessageKey) }}
    </p>
    <p class="action-error" *ngIf="filterError">
      {{ filterError.message }}
    </p>
  </div>

  <section class="dates-preview" data-section-id="homeia_component_dates_preview" *ngIf="previewDates.length">
    <div class="dates-preview__header">
      <h3>{{ lang.translate('home.datesSummary.previewTitle') }}</h3>
      <span>{{ lang.translate('home.datesSummary.previewSubtitle') }}</span>
    </div>
    <ul class="dates-preview__list" data-section-id="homeia_component_dates_preview_list">
      <li
        class="dates-preview__item"
        *ngFor="let date of previewDates; let idx = index; trackBy: trackDate"
        [attr.data-section-id]="'homeia_component_dates_preview_item_' + idx"
      >
        <div class="dates-preview__main">
          <div class="dates-preview__title">
            <span class="dates-preview__doc">{{ date.documentName }}</span>
            <span class="dates-preview__date">{{ date.dueDate | date: 'mediumDate' }}</span>
          </div>
          <div class="dates-preview__tags">
            <span class="badge" *ngIf="translateRole(date.role) as roleLabel">{{ roleLabel }}</span>
            <span class="badge state">{{ translateState(date.state, date.stateLabel) }}</span>
            <span class="badge reason" *ngIf="date.triggerReason">{{ date.triggerReason }}</span>
          </div>
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onViewDatesPanel()">
          {{ lang.translate('home.datesSummary.previewAction') }}
        </button>
      </li>
    </ul>
    <div class="dates-preview__cta">
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onChangeRole()">
        {{ lang.translate('home.datesSummary.previewChangeRole') }}
      </button>
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onOpenCalendar()">
        {{ lang.translate('home.datesSummary.previewOpenCalendar') }}
      </button>
    </div>
  </section>

  <ng-container *ngIf="{ role: (selectedRole$ | async) } as ctx">
    <ul class="role-breakdown" data-section-id="homeia_component_dates_role_breakdown">
      <li *ngFor="let entry of roleEntries; let idx = index" [attr.data-section-id]="'homeia_component_dates_role_item_' + entry.role">
        <button
          type="button"
          class="role-chip"
          (click)="onRoleToggle(entry.role)"
          [class.is-active]="ctx.role === entry.role"
          [attr.aria-pressed]="ctx.role === entry.role"
          [disabled]="filterSaving"
        >
          <div class="role-info">
            <span class="role-label">{{ translateRole(entry.role) }}</span>
            <span class="role-meta">{{ lang.translate('home.datesSummary.roleLabel') }}</span>
          </div>
          <div class="role-metrics">
            <span class="role-count">{{ entry.count }}</span>
            <span class="role-indicator" aria-hidden="true" *ngIf="ctx.role === entry.role">
              <i class="fa fa-check"></i>
            </span>
          </div>
        </button>
      </li>
    </ul>

    <div class="role-filter-summary" data-section-id="homeia_component_dates_role_filter" *ngIf="ctx.role">
      <span class="filter-chip">{{ translateRole(ctx.role) }}</span>
      <p>{{ lang.translate('home.datesSummary.panelFilterActive') }}</p>
      <button
        class="clear-filter"
        type="button"
        (click)="clearRoleFilter()"
        [disabled]="filterSaving"
      >
        {{ lang.translate('home.datesSummary.clearFilter') }}
      </button>
    </div>
  </ng-container>

</section>

<ng-template #datesState>
  <div class="error-state" *ngIf="error; else datesNonError">
    <p class="error-title">{{ error.title }}</p>
    <p class="error-message">{{ error.message }}</p>
    <div class="error-actions">
      <button type="button" class="btn btn-outline-primary" (click)="onRetry()">
        {{ lang.translate('home.general.retry') }}
      </button>
      <span class="retry-after" *ngIf="error.retryAfterSeconds">
        {{ lang.translate('home.general.retryAfter') }} {{ error.retryAfterSeconds }}s
      </span>
    </div>
  </div>
  <ng-template #datesNonError>
    <div class="empty-state" *ngIf="empty; else datesLoading">
      {{ lang.translate('home.datesSummary.empty') }}
    </div>
    <ng-template #datesLoading>
      <div class="dates-loading" role="status">{{ lang.translate('home.general.loading') }}</div>
    </ng-template>
  </ng-template>
</ng-template>

<ng-container *ngIf="(datesPanelOpen$ | async) && !loading && !error && summary">
  <div class="dates-panel-overlay" (click)="onCloseDatesPanel()">
    <section
      class="dates-panel"
      data-section-id="homeia_dashboard_dates_panel"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="lang.translate('home.datesSummary.panelTitle')"
      (click)="onPanelInteraction($event)"
    >
      <header class="panel-header">
        <div>
          <p class="panel-eyebrow">{{ lang.translate('home.datesSummary.panelEyebrow') }}</p>
          <h3>{{ lang.translate('home.datesSummary.panelTitle') }}</h3>
          <p class="panel-copy">{{ lang.translate('home.datesSummary.panelSubtitle') }}</p>
        </div>
        <button type="button" class="panel-close" (click)="onCloseDatesPanel()" [attr.aria-label]="lang.translate('home.datesSummary.closePanel')">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <ng-container *ngIf="{ role: (selectedRole$ | async) } as ctx">
        <div class="panel-filter-summary" *ngIf="ctx.role">
          <p>{{ lang.translate('home.datesSummary.panelFilterActive') }}</p>
          <span class="filter-chip">{{ translateRole(ctx.role) }}</span>
        </div>

        <div class="panel-body" data-section-id="homeia_dashboard_dates_panel_body" *ngIf="monitoredDates.length; else datesEmpty">
          <article
            class="date-item"
            *ngFor="let date of monitoredDates; let idx = index; trackBy: trackDate"
            [attr.data-section-id]="'homeia_dashboard_dates_panel_item_' + idx"
            [class.is-muted]="ctx.role && date.role !== ctx.role"
            [hidden]="ctx.role && date.role !== ctx.role"
          >
            <div class="item-heading">
              <div class="item-title-block">
                <p class="item-doc">{{ date.documentName }}</p>
                <div class="item-meta-tags">
                  <span class="badge role" *ngIf="translateRole(date.role) as roleLabel">{{ roleLabel }}</span>
            <span class="badge state">{{ translateState(date.state, date.stateLabel) }}</span>
                </div>
              </div>
              <div class="item-date-block">
                <span class="item-date">{{ date.dueDate | date: 'mediumDate' }}</span>
                <span class="item-owner">{{ date.owner }}</span>
              </div>
            </div>
            <p class="item-tooltip">{{ date.tooltip }}</p>
            <div class="item-footer">
              <span class="badge reason">{{ date.triggerReason }}</span>
            </div>
          </article>
          <p class="filter-note" *ngIf="ctx.role">
            {{ lang.translate('home.datesSummary.panelFilterActive') }}
          </p>
        </div>
      </ng-container>
    </section>
  </div>
</ng-container>

<ng-template #datesEmpty>
  <p class="empty-state">{{ lang.translate('home.datesSummary.panelEmpty') }}</p>
</ng-template>
